// PrefetchedBlobImage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { getCachedBlob, prefetchImageToBlob } from 'helpers/mediaPrefetch'
import { canonicalizeUrl } from 'helpers/signedUrl'

type PrefetchedBlobImageProps = {
  url: string
  root?: Element | null
  rootMargin?: string
  placeholder?: React.ReactNode
  alt?: string
  className?: string
  imgClassName?: string
  onClick?: () => void
  disabled?: boolean
  prefetchUrls?: Array<string | null | undefined>
}

/**
 * In-flight promise cache (module-scoped).
 * Prevents virtualization churn / remounts from restarting network work.
 *
 * Keyed by UNSIGNED cache key (origin + pathname), not the raw URL.
 */
type InflightEntry = { p: Promise<string> }
const inflight = new Map<string, InflightEntry>()

async function getOrStartPrefetch(cacheKey: string, start: () => Promise<string>): Promise<string> {
  const existing = inflight.get(cacheKey)
  if (existing) return await existing.p

  const p = start().finally(() => {
    inflight.delete(cacheKey)
  })

  inflight.set(cacheKey, { p })
  return await p
}

export default function PrefetchedBlobImage({
  url,
  root = null,
  rootMargin = '400px 0px 400px 0px',
  placeholder = null,
  alt = '',
  className,
  imgClassName,
  onClick,
  disabled,
  prefetchUrls = [],
}: PrefetchedBlobImageProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const firedRef = useRef(false)

  // Normalize URL to a stable cache key (no query/hash).
  // This prevents signed URLs from producing new cache entries.
  const cacheKey = useMemo(() => canonicalizeUrl(url) ?? '', [url])

  // Used to ignore stale promise resolutions when url changes mid-flight.
  const reqIdRef = useRef(0)

  const [blobSrc, setBlobSrc] = useState<string | null>(() => (cacheKey ? getCachedBlob(cacheKey) : null))

  const ioOptions = useMemo(() => ({ root, rootMargin }), [root, rootMargin])

  // Reset on url change; show cached immediately if present.
  useEffect(() => {
    firedRef.current = false
    reqIdRef.current += 1

    setBlobSrc(cacheKey ? getCachedBlob(cacheKey) : null)
  }, [cacheKey])

const extraPrefetchKey = useMemo(() => {
  return prefetchUrls
    .filter((u): u is string => typeof u === 'string' && u.length > 0)
    .map(u => u.trim())
    .filter(Boolean)
    .join('|')
}, [prefetchUrls])

useEffect(() => {
  if (disabled) return
  if (!extraPrefetchKey) return

  const urls = extraPrefetchKey
    .split('|')
    .filter(u => canonicalizeUrl(u) !== cacheKey)

  urls.forEach(u => {
    prefetchImageToBlob(u).catch(() => {})
  })
}, [extraPrefetchKey, cacheKey, disabled])

  useEffect(() => {
    if (!url || disabled) return
    if (!cacheKey) return

    const el = wrapperRef.current
    if (!el) return

    const myReqId = reqIdRef.current

    // Cache hit -> render immediately.
    const cached = getCachedBlob(cacheKey)
    if (cached) {
      firedRef.current = true
      setBlobSrc(cached)
      return
    }

    // If already inflight (from previous mount), subscribe and skip IO.
    const existing = inflight.get(cacheKey)
    if (existing) {
      firedRef.current = true
      existing.p
        .then((src) => {
          if (reqIdRef.current !== myReqId) return
          setBlobSrc(src || getCachedBlob(cacheKey))
        })
        .catch(() => {})
      return
    }

    const ac = new AbortController()

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry?.isIntersecting) return
        if (firedRef.current) return

        firedRef.current = true
        io.disconnect()

        // Start prefetch keyed by UNSIGNED cacheKey,
        // but fetch with the real (possibly signed) URL.
        getOrStartPrefetch(cacheKey, async () => await prefetchImageToBlob(url, ac.signal))
          .then((src) => {
            if (reqIdRef.current !== myReqId) return
            // Prefer returned value, but fall back to cache if helper writes there.
            setBlobSrc(src || getCachedBlob(cacheKey))
          })
          .catch(() => {
            // swallow (optional: set error state)
          })
      },
      { root: ioOptions.root, rootMargin: ioOptions.rootMargin, threshold: 0 }
    )

    io.observe(el)

    return () => {
      try {
        io.disconnect()
      } catch {}
      // Do NOT abort on unmount by default (virtualization remount churn).
      // try { ac.abort() } catch {}
    }
  }, [url, cacheKey, disabled, ioOptions])
  const displaySrc = blobSrc || url
return (
  <div ref={wrapperRef} className={className} onClick={onClick}>
    {displaySrc ? (
      <img src={displaySrc} alt={alt} className={imgClassName} />
    ) : (
      placeholder
    )}
  </div>
)
}
